import { ChatCircle, Plus, Lightning, Lock } from "@phosphor-icons/react";
import { LeverMark, APP_NAME } from "../../../lib/brand";
import { cn } from "../../../lib/cn";

export function DemoChatPanel({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div className="flex h-full flex-col">
      {/* Header — matches real ChatView */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-[var(--border-hairline)]">
        <div className="flex items-center gap-2">
          <div
            className="flex items-center gap-1 h-5 rounded-[var(--radius-sm)] px-1 text-[var(--text-muted)]"
          >
            <ChatCircle size={11} weight="bold" />
            <span className="text-[9px] font-semibold tabular-nums">1</span>
          </div>
          <span className="text-[10px] font-medium text-[var(--text-muted)]">
            2 messages
          </span>
        </div>
        <div
          className="flex items-center justify-center h-5 w-5 rounded-[var(--radius-sm)] text-[var(--text-muted)] opacity-40"
        >
          <Plus size={11} weight="bold" />
        </div>
      </div>

      {/* Messages — matches real ChatMessage layout */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-1">
        {/* User message: right-aligned bubble, no avatar */}
        <div className="flex justify-end px-3 py-1.5">
          <div className="max-w-[85%] rounded-[var(--radius-md)] bg-[var(--bg-surface-hover)] px-2.5 py-1.5 text-[11px] text-[var(--text-primary)] whitespace-pre-wrap break-words">
            Can you help me improve the introduction section?
          </div>
        </div>

        {/* Assistant message: left-aligned with LeverMark avatar */}
        <div className="flex gap-2 px-3 py-1.5">
          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full mt-0.5 bg-[var(--bg-surface-hover)] text-[var(--text-muted)]">
            <LeverMark size={11} />
          </div>
          <div className="flex-1 min-w-0 space-y-0.5">
            <div className="pr-2">
              <div className="prose-chat text-[11px] text-[var(--text-secondary)] leading-relaxed">
                I can help you craft a stronger introduction. Based on your document structure, I&apos;d suggest:
                <ul className="mt-1.5 ml-3 space-y-0.5 list-disc text-[11px] text-[var(--text-secondary)]">
                  <li>Opening with broader context for your research area</li>
                  <li>Stating the specific gap your work addresses</li>
                  <li>Previewing the paper&apos;s structure</li>
                </ul>
                <p className="mt-1.5">Want me to draft a revised version?</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Input — matches real ChatInput structure */}
      <div className="border-t border-[var(--border-hairline)] p-2">
        {/* Quick actions */}
        <div className="flex gap-1.5 mb-2 flex-wrap">
          {[
            { label: "Fix errors", icon: Lightning },
            { label: "Improve writing", icon: Lightning },
          ].map((action) => (
            <button
              key={action.label}
              onClick={onSignIn}
              className={cn(
                "flex items-center gap-1 rounded-[var(--radius-sm)] px-2 py-1 text-[10px] font-medium",
                "bg-[var(--bg-surface-hover)] text-[var(--text-muted)]",
                "hover:text-[var(--text-primary)]",
                "border border-[var(--border-hairline)]",
                "transition-colors",
              )}
            >
              <action.icon size={10} weight="bold" />
              {action.label}
            </button>
          ))}
        </div>

        {/* Locked input */}
        <div className="flex items-end gap-1.5">
          <button
            onClick={onSignIn}
            className={cn(
              "flex-1 text-left rounded-[var(--radius-md)] border border-dashed border-[var(--border-default)]",
              "bg-[var(--bg-surface)] px-3 py-2 text-xs text-[var(--text-muted)]",
              "hover:border-[var(--border-strong)] hover:text-[var(--text-secondary)]",
              "transition-colors cursor-pointer",
            )}
          >
            <span className="flex items-center gap-1.5">
              <Lock size={10} weight="bold" />
              Sign in to ask {APP_NAME} anything...
            </span>
          </button>
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--bg-surface-hover)] text-[var(--text-muted)] border border-[var(--border-hairline)] opacity-40"
          >
            <svg width={14} height={14} viewBox="0 0 256 256" fill="currentColor">
              <path d="M128,176a48.05,48.05,0,0,0,48-48V64a48,48,0,0,0-96,0v64A48.05,48.05,0,0,0,128,176ZM96,64a32,32,0,0,1,64,0v64a32,32,0,0,1-64,0Zm40,143.6V232a8,8,0,0,1-16,0V207.6A80.11,80.11,0,0,1,48,128a8,8,0,0,1,16,0,64,64,0,0,0,128,0,8,8,0,0,1,16,0A80.11,80.11,0,0,1,136,207.6Z" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
